import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { api } from '../../api/client';
import { errorMessageKey, unwrap } from '../../api/api-error';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';
import { useMyWorkspaces, WORKSPACES_KEY } from '../../workspaces/workspaces-api';
import { InboxSection } from './inbox-section';
import { RoleBadge } from './role-badge';
import { paths } from '../../app/paths';

export const workspaceNameSchema = z.object({
  name: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
});
type NameValues = z.infer<typeof workspaceNameSchema>;

/** Мои пространства, создание нового и «входящие» приглашения. */
export function WorkspacesPage() {
  const { t } = useI18n();
  const workspaces = useMyWorkspaces();
  const items = workspaces.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <section className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('workspaces.title')}</h1>
      <InboxSection />
      <Card title={t('workspaces.mine')}>
        {workspaces.isError && (
          <Notice tone="danger">{t(errorMessageKey(workspaces.error))}</Notice>
        )}
        <ul className="divide-y divide-border text-sm">
          {items.map((workspace) => (
            <li key={workspace.id} className="flex items-center justify-between gap-4 py-2">
              <Link
                to={paths.workspace(workspace.id)}
                className="truncate text-primary hover:underline"
              >
                {workspace.name}
              </Link>
              <span className="flex items-center gap-2 text-2xs text-muted-foreground">
                {workspace.isPersonal && <span>{t('workspaces.personal')}</span>}
                {workspace.archived && <span>{t('workspaces.archived')}</span>}
                <RoleBadge role={workspace.role} />
              </span>
            </li>
          ))}
        </ul>
        {workspaces.hasNextPage && (
          <Button
            type="button"
            variant="secondary"
            disabled={workspaces.isFetchingNextPage}
            onClick={() => void workspaces.fetchNextPage()}
          >
            {t('common.showMore')}
          </Button>
        )}
      </Card>
      <CreateWorkspace />
    </section>
  );
}

function CreateWorkspace() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<NameValues>({
    resolver: zodResolver(workspaceNameSchema),
    defaultValues: { name: '' },
  });
  const create = useMutation({
    mutationFn: async (values: NameValues) =>
      unwrap(await api.POST('/api/v1/workspaces', { body: values })),
    onSuccess: async (workspace) => {
      await queryClient.invalidateQueries({ queryKey: WORKSPACES_KEY });
      await navigate(paths.workspace(workspace.id));
    },
  });

  return (
    <Card title={t('workspaces.create.title')}>
      <form
        className="flex items-end gap-3"
        onSubmit={form.handleSubmit((values) => create.mutate(values))}
        noValidate
      >
        <div className="flex-1">
          <TextField control={form.control} name="name" label={t('workspaces.field.name')} />
        </div>
        <Button type="submit" disabled={create.isPending}>
          {t('workspaces.create.submit')}
        </Button>
      </form>
      {create.isError && <Notice tone="danger">{t(errorMessageKey(create.error))}</Notice>}
    </Card>
  );
}
